import type { ChangeEvent } from 'react';
import { forwardRef, useCallback } from 'react';
import { FileSelect } from '@wistia/vhs';
import { trackStructEvent } from '~/utilities/snowplow';
import type { UploaderFunctions } from '~/singlePageApp/components/uploader/types';
import { useUploadContext } from '~/singlePageApp/components/uploader/useUploadContext';
import { isNonEmptyArray, isNonEmptyRecord, isNotNil, isNotNull } from '~/utilities/type-guards';
import { acceptedAllFileExtensions } from '~/utilities/acceptedFileTypes';

export const UploadToVMAFileSelect = forwardRef<
  HTMLInputElement,
  {
    accept?: string;
    children?: React.ReactNode;
    'data-testid'?: string;
    disabled?: boolean;
    getProjectMetadata?: () => Promise<{ projectUUID: string; projectId: string }>;
    id?: string;
    multiple?: boolean;
    onError?: (error: Error) => void;
    onUploadStart?: () => void;
    startUploadOptions?: Parameters<UploaderFunctions['startUpload']>[1];
    updateQueueOptions?: Parameters<UploaderFunctions['updateQueue']>[2];
    uploadContainerId: string;
    snowplowLabel?: string;
    onBeforeUpload?: (files: File[]) => File[];
    onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
    onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  }
>(
  (
    {
      accept = acceptedAllFileExtensions,
      children,
      'data-testid': dataTestId,
      disabled,
      getProjectMetadata,
      id,
      multiple,
      onError,
      onUploadStart,
      startUploadOptions = {},
      updateQueueOptions = {},
      uploadContainerId,
      snowplowLabel,
      onBeforeUpload,
      ...fileSelectForwardedProps
    },
    ref,
  ) => {
    const { updateQueue, startUpload } = useUploadContext(uploadContainerId);

    const onChange = useCallback(
      async (event: ChangeEvent<HTMLInputElement>) => {
        let files = [...(event.target.files ?? [])];

        if (isNotNil(onBeforeUpload) && isNonEmptyArray(files)) {
          files = onBeforeUpload(files);
        }

        if (isNonEmptyArray(files)) {
          if (isNotNull(snowplowLabel)) {
            trackStructEvent({
              category: 'file',
              action: 'upload',
              label: snowplowLabel,
              value: files.length,
            });
          }
          const { onFileUploaded, uploadMetadata } = startUploadOptions;
          const projectMetadata = isNotNil(getProjectMetadata) ? await getProjectMetadata() : {};
          const metadataWithProject = {
            ...(isNotNil(uploadMetadata) && uploadMetadata),
            ...projectMetadata,
          };

          updateQueue(files, updateQueueOptions);
          startUpload({
            onFileUploaded,
            ...(isNonEmptyRecord(metadataWithProject) && { uploadMetadata: metadataWithProject }),
          });
          onUploadStart?.();
        }

        event.target.value = '';
      },
      [
        getProjectMetadata,
        onBeforeUpload,
        onUploadStart,
        snowplowLabel,
        startUpload,
        startUploadOptions,
        updateQueue,
        updateQueueOptions,
      ],
    );

    const onChangeVoid = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        void onChange(event);
      },
      [onChange],
    );

    return (
      <FileSelect
        ref={ref}
        accept={accept}
        data-testid={dataTestId}
        disabled={disabled}
        onError={onError}
        onChange={onChangeVoid}
        multiple={multiple}
        id={id ?? `${uploadContainerId}-file-select`}
        {...fileSelectForwardedProps}
      >
        {children}
      </FileSelect>
    );
  },
);
